import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchMenusByRestaurant, fetchRestoDataByUser } from "../services/restaurantService";
import { fetchCategories, fetchProducts } from "../services/MenuService";
import { hasStoredWizardDraft } from "../jsx/components/Setup/StartNowWizard/draftStorage";
import {
  ONBOARDING_TOUR_MODULES,
  getModuleStepStatus,
} from "../config/onboardingTourConfig";
import { loadTourProgress } from "../utils/onboardingTourProgress";
import { hasOnboardingQrGenerated } from "../utils/onboardingTutorialStorage";

async function loadTourContext() {
  const [restoResult, menusResult, categoriesResult, productsResult] = await Promise.allSettled([
    fetchRestoDataByUser(),
    fetchMenusByRestaurant(),
    fetchCategories(),
    fetchProducts(),
  ]);

  const restaurant =
    restoResult.status === "fulfilled" ? restoResult.value?.restaurant : null;

  let menus = [];
  if (menusResult.status === "fulfilled" && Array.isArray(menusResult.value)) {
    menus = menusResult.value;
  }

  const categories =
    categoriesResult.status === "fulfilled" && Array.isArray(categoriesResult.value)
      ? categoriesResult.value
      : [];
  const products =
    productsResult.status === "fulfilled" && Array.isArray(productsResult.value)
      ? productsResult.value
      : [];

  const progress = loadTourProgress();

  return {
    restaurant,
    menus,
    hasCategories: categories.length > 0,
    hasProducts: products.length > 0,
    qrGenerated: hasOnboardingQrGenerated(),
    wizardDraft: hasStoredWizardDraft(),
    visitedStepIds: progress.visitedStepIds,
  };
}

export function useOnboardingTourModules({ refreshKey = 0 } = {}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem("tokenLogin")) {
      setCtx(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const nextCtx = await loadTourContext();
      setCtx(nextCtx);
    } catch (error) {
      console.error("Tour modules load error:", error);
      setCtx(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh, refreshKey]);

  useEffect(() => {
    const handleProgress = () => refresh();
    window.addEventListener("onboarding-tour:progress", handleProgress);
    return () => window.removeEventListener("onboarding-tour:progress", handleProgress);
  }, [refresh]);

  const modules = useMemo(() => {
    if (!ctx) return [];

    return ONBOARDING_TOUR_MODULES.map((module) => {
      const status = getModuleStepStatus(module, ctx);
      return {
        id: module.id,
        icon: module.icon,
        title: t(module.titleKey),
        description: t(module.descKey),
        totalSteps: status.totalSteps,
        viewedSteps: status.viewedSteps,
        complete: status.complete,
        inProgress: status.inProgress,
      };
    });
  }, [ctx, t]);

  const completedCount = modules.filter((module) => module.complete).length;
  const totalModules = modules.length;
  const progress =
    totalModules > 0 ? Math.round((completedCount / totalModules) * 100) : 0;
  const allComplete = totalModules > 0 && completedCount === totalModules;
  const currentModule = modules.find((module) => !module.complete) || null;

  return {
    loading,
    modules,
    progress,
    allComplete,
    currentModule,
    completedCount,
    totalModules,
    refresh,
  };
}
